import { useState, useEffect, useMemo } from 'react';
import { Loader2, Play, X, Clapperboard, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const USAGE_STYLE = {
  live: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  unused: 'bg-slate-50 text-slate-500 border-slate-200',
};
const USAGE_LABEL = { live: 'Live on site', draft: 'Draft only', unused: 'Not used' };

const fmtSize = (b) => `${(b / 1048576).toFixed(1)} MB`;

const UsageBadge = ({ usage, testId }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full border text-2xs font-bold ${USAGE_STYLE[usage]}`} data-testid={testId}>
    {USAGE_LABEL[usage]}
  </span>
);

const VideoRow = ({ v, open, onToggle }) => (
  <div className="py-4 border-b border-slate-100 last:border-0" data-testid={`media-row-${v.id}`}>
    <div className="flex items-start gap-3">
      <button
        type="button"
        onClick={onToggle}
        className="group relative shrink-0 w-32 aspect-video rounded-lg overflow-hidden bg-slate-900"
        aria-label={open ? 'Close preview' : 'Preview video'}
        data-testid={`media-preview-btn-${v.id}`}
      >
        {v.poster_path && (
          <img
            src={`${API}/api/media/posters/${v.id}.webp?size=thumb`}
            alt=""
            width={128}
            height={72}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`media-poster-${v.id}`}
          />
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/40 transition-colors">
          <span className="w-8 h-8 rounded-full bg-white/90 text-taxera-charcoal flex items-center justify-center">
            {open ? <X className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </span>
        </span>
      </button>
      <a
        href={`${API}/api/media/videos/${v.id}.mp4?download=true`}
        download={`taxera-${v.id}.mp4`}
        className="shrink-0 w-9 h-9 rounded-full border border-slate-200 text-slate-500 flex items-center justify-center hover:border-taxera-blue hover:text-taxera-blue transition-colors"
        aria-label="Download video"
        title={`Download (${fmtSize(v.size_bytes)})`}
        data-testid={`media-download-btn-${v.id}`}
      >
        <Download className="w-4 h-4" />
      </a>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-mono text-xs font-semibold text-taxera-charcoal">{v.id}</span>
          <UsageBadge usage={v.usage} testId={`media-usage-${v.id}`} />
          <span className="text-2xs uppercase tracking-wide font-bold text-slate-400">{v.role}</span>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed" data-testid={`media-desc-${v.id}`}>{v.description}</p>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-2xs text-slate-400">
          <span>{v.duration_s}s · {v.width}x{v.height} · {v.has_audio ? 'with sound' : 'silent'} · {fmtSize(v.size_bytes)}</span>
          <span>File: {v.original_paths.join(', ')}</span>
          {v.used_by.length > 0 && (
            <span data-testid={`media-used-by-${v.id}`}>
              Used by: {v.used_by.map((u) => `${u.kind} "${u.title || u.id}" (${u.status})`).join(', ')}
            </span>
          )}
        </div>
        {open && (
          <video
            src={`${API}/api/media/videos/${v.id}.mp4`}
            poster={v.poster_path ? `${API}/api/media/posters/${v.id}.webp` : undefined}
            controls
            autoPlay
            muted
            playsInline
            className="mt-3 w-full max-w-[560px] max-h-[420px] rounded-xl bg-black"
            data-testid={`media-player-${v.id}`}
          />
        )}
      </div>
    </div>
  </div>
);

const Filters = ({ usage, setUsage, counts }) => (
  <div className="flex flex-wrap gap-2" data-testid="media-filters">
    {['all', 'live', 'draft', 'unused'].map((k) => (
      <button
        key={k}
        type="button"
        onClick={() => setUsage(k)}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${usage === k ? 'bg-taxera-charcoal text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
        data-testid={`media-filter-${k}`}
      >
        {k === 'all' ? 'All' : USAGE_LABEL[k]} ({counts[k] || 0})
      </button>
    ))}
  </div>
);

const MediaLibraryTab = ({ headers }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState('all');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/admin/media/videos`, { headers, credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setVideos(d.videos || []))
      .catch((s) => setError(`Could not load the media library (${s}).`))
      .finally(() => setLoading(false));
  }, [headers]);

  const counts = useMemo(() => videos.reduce((acc, v) => ({ ...acc, all: acc.all + 1, [v.usage]: (acc[v.usage] || 0) + 1 }), { all: 0 }), [videos]);
  const families = useMemo(() => {
    const groups = {};
    videos.filter((v) => usage === 'all' || v.usage === usage).forEach((v) => { (groups[v.family] ||= []).push(v); });
    return Object.entries(groups);
  }, [videos, usage]);

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>;
  if (error) return <p className="text-sm text-red-600" data-testid="media-error">{error}</p>;

  return (
    <div className="space-y-6" data-testid="media-library-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-taxera-charcoal flex items-center gap-2">
            <Clapperboard className="w-5 h-5" /> Media library
          </h2>
          <p className="text-sm text-slate-500">Every brand video, one copy each, stored in Object Storage. Read-only.</p>
        </div>
        <Filters usage={usage} setUsage={setUsage} counts={counts} />
      </div>
      {families.map(([family, items]) => (
        <section key={family} className="bg-white rounded-2xl border border-slate-200 px-5" data-testid={`media-family-${family}`}>
          <h3 className="pt-4 pb-1 text-xs font-bold uppercase tracking-wide text-slate-400">{family} · {items.length}</h3>
          {items.map((v) => (
            <VideoRow key={v.id} v={v} open={openId === v.id} onToggle={() => setOpenId(openId === v.id ? null : v.id)} />
          ))}
        </section>
      ))}
    </div>
  );
};

export default MediaLibraryTab;
